import { useEffect, useState, useCallback } from 'react';
import { apiService } from '../services/apiService';
import { EventsDashboard, EventDetail, ExpiryData } from '../types';

type PeriodType = 'today' | 'tomorrow' | 'week';

/** Returns Monday of the current week in YYYY-MM-DD. */
function getCurrentWeekStart(): string {
  const now = new Date();
  const day = now.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + mondayOffset);
  return monday.toISOString().slice(0, 10);
}

export function useEventsList(ownershipScope: 'group' | 'self' = 'group') {
  const [selectedWeekStart, setSelectedWeekStart] = useState<string>(getCurrentWeekStart);
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('week');
  const [dashboard, setDashboard] = useState<EventsDashboard | null>(null);
  const [expiryQuarters, setExpiryQuarters] = useState<ExpiryData[]>([]);
  const [events, setEvents] = useState<EventDetail[]>([]);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSummaryData = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const [dashboardData, expiryData] = await Promise.all([
        apiService.getEventsDashboard(getCurrentWeekStart(), false, ownershipScope),
        apiService.getProductsNearExpiry(false, ownershipScope)
      ]);
      setDashboard(dashboardData);
      setExpiryQuarters(expiryData);
    } finally {
      setLoadingSummary(false);
    }
  }, [ownershipScope]);

  const fetchEventsData = useCallback(async () => {
    setLoadingEvents(true);
    try {
      const eventsData = await apiService.getEventsList(selectedWeekStart, false, ownershipScope);
      setEvents(eventsData);
    } finally {
      setLoadingEvents(false);
    }
  }, [selectedWeekStart, ownershipScope]);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      await Promise.all([fetchSummaryData(), fetchEventsData()]);
    } catch (err) {
      console.error('Error fetching events data:', err);
      setError('Error loading events data');
    }
  }, [fetchSummaryData, fetchEventsData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    // On week change we only need to refetch event list.
    fetchEventsData().catch((err) => {
      console.error('Error fetching events list:', err);
      setError('Error loading events data');
    });
  }, [selectedWeekStart, fetchEventsData]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      setError(null);
      await Promise.all([fetchSummaryData(), fetchEventsData()]);
    } finally {
      setRefreshing(false);
    }
  }, [fetchSummaryData, fetchEventsData]);

  const filteredEvents = events.filter((event) => {
    const eventDate = new Date(event.eventDate);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    switch (selectedPeriod) {
      case 'today':
        return eventDate.toDateString() === today.toDateString();
      case 'tomorrow':
        return eventDate.toDateString() === tomorrow.toDateString();
      case 'week':
        return true;
      default:
        return true;
    }
  });

  return {
    selectedWeekStart,
    setSelectedWeekStart,
    selectedPeriod,
    setSelectedPeriod,
    dashboard,
    expiryQuarters,
    loading: loadingSummary || loadingEvents,
    loadingSummary,
    loadingEvents,
    refreshing,
    error,
    filteredEvents,
    onRefresh,
    refetch: fetchData,
  };
}


